// ============================================================================
// FILE:    src/app/core/auth/capabilities.enum.ts
// VERSION: v2.0.0 — RBAC Phase 2: vollständige Capability-Liste
// DATE:    2026-05-09
//
// ZWECK:
//   Zentrale Liste aller Feature-Capabilities im System.
//   Eine Capability = eine konkrete Funktion die ein User ausführen darf.
//
// VERWENDUNG:
//   1. role-capabilities.map.ts  → welche Rolle hat welche Capabilities
//   2. page-access.config.ts     → welche Page braucht welche Capability
//   3. has-capability.directive  → *hasCapability="'SPRINT_CREATE'" im Template
//   4. projectAccessGuard        → blockt Navigation wenn Capability fehlt
//
// NAMENSKONVENTION:
//   OBJEKT_AKTION — z.B. SPRINT_CREATE, ITEM_VIEW, DAILY_OWN_EDIT
//   Org-Capabilities beginnen mit einem Domänen-Begriff ohne PROJEKT_-Prefix.
//
// WARUM KEIN *ngIf="permission >= 3" MEHR:
//   Weil permission-Schwellen im Template hart verdrahtet sind und bei einer
//   Rollen-Umstrukturierung alle Templates manuell geändert werden müssten.
//   Capabilities sind eine stabile semantische Schicht — die Zahl dahinter
//   (welche Rolle welche Capability hat) ändert sich in role-capabilities.map.ts,
//   nicht in hundert Templates.
//
// ORG-SCOPE CAPABILITIES (nicht projektbezogen):
//   APP_MANAGEMENT_ACCESS  → darf /app-management öffnen (orgPerm >= 3)
//   USER_PROVISION         → darf Bulk-Provisioning nutzen (orgPerm >= 3)
//   THIRD_PARTY_CONFIG     → darf Thirdparty-Config (orgPerm >= 3)
//   OWN_PROFILE_VIEW       → darf eigenes Profil sehen (orgPerm >= 1)
//   RT_ASSESSMENT_OWN      → darf eigenen RT-Test machen (perm >= 1 in Projekt
//                            ODER orgPerm >= 1 für org-scope)
//
// PROJECT-SCOPE CAPABILITIES (pslot-bezogen):
//   Alle SPRINT_*, ITEM_*, TASK_*, MILESTONE_*, PERSONA_*, SOLUTION_*,
//   DAILY_*, DASHBOARD_*, PROJECT_ACCESS_MANAGE
// ============================================================================
// ============================================================================
// FILE:    src/app/core/auth/capabilities.enum.ts
// VERSION: v2.1.0 — Bugfix: CAPABILITY_META Export hinzugefügt
// DATE:    2026-05-10
//
// ÄNDERUNG v2.1.0:
//   CAPABILITY_META Map ergänzt — wird von project-access.guard.ts importiert
//   um bei einem Guard-Deny die fehlende Capability lesbar zu beschreiben.
//   Import-Fehler: "has no exported member 'CAPABILITY_META'" behoben.
//
//   Struktur pro Eintrag:
//     label:       kurzer Name für die /insufficient-permission Page
//     description: was diese Capability bedeutet (Erklärung für den User)
//     typicalRole: welche Rolle man typischerweise braucht
//
// Alle Capability-Einträge unverändert gegenüber v2.0.0.
// ============================================================================

export enum Capability {

  // ── Org-Scope: App-Verwaltung ─────────────────────────────────────────────

  /** Zugang zum App-Management Portal. Trigger: orgPerm >= 3 (PMO_OFFICER). */
  APP_MANAGEMENT_ACCESS = 'APP_MANAGEMENT_ACCESS',

  /** Bulk User Provisioning. Trigger: orgPerm >= 3 (PMO_OFFICER). */
  USER_PROVISION = 'USER_PROVISION',

  /**
   * Thirdparty-Config (/thirdparty-config).
   * Trigger: orgPerm >= 3 (PMO_OFFICER).
   */
  THIRD_PARTY_CONFIG = 'THIRD_PARTY_CONFIG',

  /**
   * Eigenes Profil ansehen/bearbeiten (/my-profile).
   * Trigger: orgPerm >= 1 (jeder eingeloggte User mit CAL).
   */
  OWN_PROFILE_VIEW = 'OWN_PROFILE_VIEW',

  // ── Org + Projekt-Scope: Persönliche Funktionen ──────────────────────────

  /**
   * Riemann-Thomann Test durchführen.
   * Trigger: PROJECT_MEMBER+ im Projekt ODER orgPerm >= 1.
   * Viewer hat diese Capability NICHT.
   */
  RT_ASSESSMENT_OWN = 'RT_ASSESSMENT_OWN',

  // ── Projekt-Scope: Lesen / Ansehen ───────────────────────────────────────

  /** Sprint-Übersicht ansehen. Trigger: PROJECT_VIEWER+. */
  SPRINT_VIEW = 'SPRINT_VIEW',

  /** Dashboard ansehen (Metrics, KPIs, Quick Stats). Trigger: PROJECT_VIEWER+. */
  DASHBOARD_VIEW = 'DASHBOARD_VIEW',

  /** Items / PBIs ansehen. Trigger: PROJECT_VIEWER+. */
  ITEM_VIEW = 'ITEM_VIEW',

  /** Tasks suchen (Task Seeker). Trigger: PROJECT_VIEWER+. */
  TASK_SEEK = 'TASK_SEEK',

  /** Items + Features suchen (Item Seeker). Trigger: PROJECT_VIEWER+. */
  ITEM_SEEK = 'ITEM_SEEK',

  /** Personas ansehen. Trigger: PROJECT_VIEWER+. */
  PERSONA_VIEW = 'PERSONA_VIEW',

  // ── Projekt-Scope: Daily Scrum (Member+) ─────────────────────────────────

  /**
   * Daily-Scrum-Matrix ansehen.
   * Trigger: PROJECT_MEMBER+ (NICHT Viewer — Daily ist Team-intern).
   */
  DAILY_VIEW = 'DAILY_VIEW',

  /**
   * Eigenen Daily-Eintrag bearbeiten.
   * Trigger: PROJECT_MEMBER+. Backend prüft UUID-Ownership.
   */
  DAILY_OWN_EDIT = 'DAILY_OWN_EDIT',

  // ── Projekt-Scope: Tasks (Member+) ───────────────────────────────────────

  /** Neuen Task erstellen. Trigger: PROJECT_MEMBER+. */
  TASK_CREATE = 'TASK_CREATE',

  /** Eigenen Task bearbeiten. Trigger: PROJECT_MEMBER+. */
  TASK_EDIT_OWN = 'TASK_EDIT_OWN',

  /** Eigenes Item bearbeiten. Trigger: PROJECT_MEMBER+. */
  ITEM_EDIT_OWN = 'ITEM_EDIT_OWN',

  // ── Projekt-Scope: Planung (IT-PM+) ──────────────────────────────────────

  /** Sprint erstellen. Trigger: PROJECT_IT_PM+. */
  SPRINT_CREATE = 'SPRINT_CREATE',

  /** Bestehenden Sprint bearbeiten. Trigger: PROJECT_IT_PM+. */
  SPRINT_EDIT = 'SPRINT_EDIT',

  /** Item / PBI erstellen. Trigger: PROJECT_IT_PM+. */
  ITEM_CREATE = 'ITEM_CREATE',

  /** Milestone / Epic erstellen. Trigger: PROJECT_IT_PM+. */
  MILESTONE_CREATE = 'MILESTONE_CREATE',

  /** Bestehenden Milestone bearbeiten. Trigger: PROJECT_IT_PM+. */
  MILESTONE_EDIT = 'MILESTONE_EDIT',

  /** Personas erstellen und verwalten. Trigger: PROJECT_IT_PM+. */
  PERSONA_MANAGE = 'PERSONA_MANAGE',

  /** WBS (PSP) Baum anzeigen. Trigger: PROJECT_MEMBER+. */
  WBS_VIEW = 'WBS_VIEW',

  /** WBS (PSP) Baum anlegen und bearbeiten. Trigger: PROJECT_IT_PM+. */
  WBS_MANAGE = 'WBS_MANAGE',

  /** SAFe Solution erstellen. Trigger: PROJECT_IT_PM+. */
  SOLUTION_CREATE = 'SOLUTION_CREATE',

  /** SAFe Solution bearbeiten. Trigger: PROJECT_IT_PM+. */
  SOLUTION_EDIT = 'SOLUTION_EDIT',

  // ── Projekt-Scope: Administration (Project Admin+) ────────────────────────

  /**
   * Projekt-Zugang verwalten (/project/:pslot/manage-access).
   * Trigger: PROJECT_ADMIN ODER orgPerm >= 3 (PMO_OFFICER org-weit).
   */
  PROJECT_ACCESS_MANAGE = 'PROJECT_ACCESS_MANAGE',
}

// ============================================================================
// CAPABILITY_META — Metadaten für jede Capability
//
// VERWENDUNG: project-access.guard.ts liest diese Map wenn ein Guard-Deny
// auftritt und gibt label/description/typicalRole als Query-Params an die
// /insufficient-permission Page weiter.
//
// ZEIGT dem User z.B.:
//   "You need: Sprint Creator (create and manage sprints)"
//   "Typical role required: IT Project Manager or higher"
// ============================================================================
export interface CapabilityMeta {
  /** Kurzer Name für die insufficient-permission Page */
  label: string;
  /** Erklärung was diese Capability bedeutet */
  description: string;
  /** Welche Projekt- oder Org-Rolle diese Capability typischerweise hat */
  typicalRole: string;
}

export const CAPABILITY_META: Record<Capability, CapabilityMeta> = {
  [Capability.APP_MANAGEMENT_ACCESS]: {
    label: 'App Management Access',
    description: 'Access to the app management portal (user and license management)',
    typicalRole: 'PMO Officer (org permission ≥ 3)',
  },
  [Capability.USER_PROVISION]: {
    label: 'User Provisioning',
    description: 'Bulk-create users and assign CAL licenses',
    typicalRole: 'PMO Officer (org permission ≥ 3)',
  },
  [Capability.THIRD_PARTY_CONFIG]: {
    label: 'Third-Party Configuration',
    description: 'Configure external integrations (email, OAuth, etc.)',
    typicalRole: 'PMO Officer (org permission ≥ 3) or Project Admin',
  },
  [Capability.OWN_PROFILE_VIEW]: {
    label: 'Profile View',
    description: 'View and edit your own profile',
    typicalRole: 'Any authenticated user with CAL',
  },
  [Capability.RT_ASSESSMENT_OWN]: {
    label: 'RT Assessment',
    description: 'Complete your own Riemann-Thomann personality assessment',
    typicalRole: 'Team Member or higher',
  },
  [Capability.SPRINT_VIEW]: {
    label: 'Sprint View',
    description: 'View sprint overview and details',
    typicalRole: 'Viewer or higher (any project member)',
  },
  [Capability.DASHBOARD_VIEW]: {
    label: 'Dashboard View',
    description: 'View project dashboard, metrics and KPIs',
    typicalRole: 'Viewer or higher (any project member)',
  },
  [Capability.ITEM_VIEW]: {
    label: 'Item View',
    description: 'View backlog items and PBIs',
    typicalRole: 'Viewer or higher (any project member)',
  },
  [Capability.TASK_SEEK]: {
    label: 'Task Seeker',
    description: 'Search tasks across the project history',
    typicalRole: 'Viewer or higher (any project member)',
  },
  [Capability.ITEM_SEEK]: {
    label: 'Item Seeker',
    description: 'Search items and features across the project',
    typicalRole: 'Viewer or higher (any project member)',
  },
  [Capability.PERSONA_VIEW]: {
    label: 'Persona View',
    description: 'View project personas',
    typicalRole: 'Viewer or higher (any project member)',
  },
  [Capability.DAILY_VIEW]: {
    label: 'Daily Scrum View',
    description: 'View the Daily Scrum Matrix and Team View',
    typicalRole: 'Team Member or higher (not Viewer)',
  },
  [Capability.DAILY_OWN_EDIT]: {
    label: 'Daily Entry Edit',
    description: 'Edit your own daily scrum entry',
    typicalRole: 'Team Member or higher',
  },
  [Capability.TASK_CREATE]: {
    label: 'Task Creator',
    description: 'Create new tasks',
    typicalRole: 'Team Member or higher',
  },
  [Capability.TASK_EDIT_OWN]: {
    label: 'Task Editor',
    description: 'Edit your own tasks',
    typicalRole: 'Team Member or higher',
  },
  [Capability.ITEM_EDIT_OWN]: {
    label: 'Item Editor',
    description: 'Edit items you are assigned to',
    typicalRole: 'Team Member or higher',
  },
  [Capability.SPRINT_CREATE]: {
    label: 'Sprint Creator',
    description: 'Create and manage sprints',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.SPRINT_EDIT]: {
    label: 'Sprint Editor',
    description: 'Edit existing sprints',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.ITEM_CREATE]: {
    label: 'Item Creator',
    description: 'Create backlog items and PBIs',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.MILESTONE_CREATE]: {
    label: 'Milestone Creator',
    description: 'Create milestones and epics',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.MILESTONE_EDIT]: {
    label: 'Milestone Editor',
    description: 'Edit existing milestones',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.PERSONA_MANAGE]: {
    label: 'Persona Manager',
    description: 'Create and manage project personas',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.WBS_VIEW]: {
    label: 'WBS View',
    description: 'View the project WBS/PSP tree',
    typicalRole: 'Project Member or higher',
  },
  [Capability.WBS_MANAGE]: {
    label: 'WBS Manage',
    description: 'Create and edit WBS/PSP nodes (PO/PM function)',
    typicalRole: 'IT Project Manager, Planner, or Project Admin',
  },
  [Capability.SOLUTION_CREATE]: {
    label: 'Solution Creator',
    description: 'Create SAFe solutions',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.SOLUTION_EDIT]: {
    label: 'Solution Editor',
    description: 'Edit SAFe solutions',
    typicalRole: 'IT Project Manager or higher',
  },
  [Capability.PROJECT_ACCESS_MANAGE]: {
    label: 'Access Management',
    description: 'Manage who can access this project and in which role',
    typicalRole: 'Project Admin or PMO Officer',
  },
};
